import type { ContentFlag, ContentFlagReason } from 'src/types/content-flag';

import useSWR, { mutate } from 'swr';
import { useMemo, useState, useCallback } from 'react';

import { poster, deleter, fetcher, getEndpoints, createEndpoints, deleteEndpoints } from 'src/utils/axios';

import { ContentFlagContentType } from 'src/types/content-flag';

/** True when the SWR key’s path string contains `contentFlag` (lists, reasons, by-guid, create URLs, etc.). */
function isContentFlagRelatedCacheKey(key: unknown): boolean {
  const path = Array.isArray(key) ? key[0] : key;
  return typeof path === 'string' && path.includes('contentFlag');
}


const swrOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

export function useGetContentFlags(contentType: ContentFlagContentType, guid: string) {
  const url = useMemo(() => {
    switch (contentType) {
      case ContentFlagContentType.STORY:
        return getEndpoints.contentFlag.listByStory(guid);
      case ContentFlagContentType.EVENT:
        return getEndpoints.contentFlag.listByEvent(guid);
      default:
        return null;
    }
  }, [guid, contentType]);


  const { data, isLoading, error, isValidating } = useSWR<{ data: ContentFlag[] }>(url, fetcher, swrOptions);

  const memoizedValue = useMemo(() => ({
    contentFlags: data?.data || [],
    contentFlagsLoading: isLoading || isValidating,
    contentFlagsError: error,
  }), [data?.data, isLoading, error, isValidating]);

  return memoizedValue;
}
export type CreateContentFlagParams = {
  reasons: number[];
  body?: string;
};

export function useGetContentFlagReasons() {
  const { data, isLoading, error, isValidating } = useSWR<{ data: ContentFlagReason[] }>(getEndpoints.contentFlag.reasons, fetcher, swrOptions);
  const memoizedValue = useMemo(() => ({
    contentFlagReasons: data?.data || [],
    contentFlagReasonsMap: data?.data?.reduce((acc, reason) => {
      acc[reason.id] = reason;
      return acc;
    }, {} as Record<number, ContentFlagReason>),
    contentFlagReasonsLoading: isLoading || isValidating,
    contentFlagReasonsError: error,
  }), [data?.data, isLoading, error, isValidating]);
  return memoizedValue;
}

export function useCreateContentFlag(contentType: ContentFlagContentType, guid: string) {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createContentFlag = useCallback(
    async (params: CreateContentFlagParams) => {
      setIsCreating(true);
      setError(null);

      try {
        const { reasons, body } = params;
        const endpoint = createEndpoints.contentFlag[contentType](guid);

        const payload = {
          reasons,
          body: body ?? undefined,
        };

        const result = await poster(endpoint, payload);
        return result.data;
      } catch (err: any) {
        const errMsg = err?.response?.data?.message || err?.message;
        setError(errMsg);
        throw new Error(errMsg);
      } finally {
        setIsCreating(false);
      }
    },
    [contentType, guid]
  );

  return {
    createContentFlag,
    isCreating,
    error,
  };
}

export function useDeleteContentFlag() {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteContentFlag = useCallback(async (contentFlagGuid: string) => {
    if (!contentFlagGuid) {
      throw new Error('Content flag GUID is required to delete content flag');
    }

    setIsDeleting(true);
    setError(null);

    try {
      const result = await deleter(deleteEndpoints.contentFlag.byGuid(contentFlagGuid));
      if (result.error) {
        setError(result.error);
        throw result.error;
      }
      return result.data;
    } catch (err: any) {
      const errMsg = err.response?.data?.message || err.message;
      setError(errMsg);
      throw new Error(errMsg);
    } finally {
      setIsDeleting(false);
    }
  }, []);

  const memoizedValue = useMemo(() => ({
    deleteContentFlag,
    isDeleting,
    error,
  }), [deleteContentFlag, isDeleting, error]);
  return memoizedValue;
}

/** Revalidates every SWR cache entry whose key contains the substring `contentFlag`. */
export async function mutateEveryContentFlagEndpoint(): Promise<void> {
  await mutate(isContentFlagRelatedCacheKey, undefined, { revalidate: true });
}